import { useEffect, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import './Lightbox.scss';

interface LightboxProps {
	/** Full-resolution image shown in the overlay. */
	src: string;
	alt: string;
	/** Optional caption under the enlarged image. */
	caption?: string;
	/** The clickable trigger content — usually a styled thumbnail frame. */
	children: ComponentChildren;
	/** Extra class on the trigger button (layout hooks per call site). */
	triggerClass?: string;
}

/**
 * Click-to-enlarge image lightbox. Wraps any thumbnail/frame as a
 * trigger; clicking opens a full-screen overlay with the high-res
 * image. Closes on backdrop click, the ✕ button, or Escape, and
 * locks body scroll while open.
 *
 * Built reusable on purpose — the marketing page will wrap more
 * screenshots in this same component over time. No portal: a fixed,
 * high-z overlay is enough to sit above the nav + aurora bg.
 */
export function Lightbox({ src, alt, caption, children, triggerClass }: LightboxProps) {
	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (!open) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setOpen(false);
		};
		document.addEventListener('keydown', onKey);
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.removeEventListener('keydown', onKey);
			document.body.style.overflow = prevOverflow;
		};
	}, [open]);

	return (
		<>
			<button
				type="button"
				class={`m-lightbox-trigger ${triggerClass ?? ''}`}
				onClick={() => setOpen(true)}
				aria-label={`Enlarge image: ${alt}`}
			>
				{children}
				<span class="m-lightbox-trigger__cue" aria-hidden="true">
					<span class="m-lightbox-trigger__cue-icon" />
					click to enlarge
				</span>
			</button>

			{open && (
				<div
					class="m-lightbox"
					role="dialog"
					aria-modal="true"
					aria-label={alt}
					onClick={() => setOpen(false)}
				>
					<button
						type="button"
						class="m-lightbox__close"
						aria-label="Close"
						onClick={() => setOpen(false)}
					>
						<span aria-hidden="true">×</span>
					</button>
					<figure class="m-lightbox__figure" onClick={(e) => e.stopPropagation()}>
						<img class="m-lightbox__img" src={src} alt={alt} />
						{caption && <figcaption class="m-lightbox__caption">{caption}</figcaption>}
					</figure>
				</div>
			)}
		</>
	);
}
